import React from 'react';

import { DynamicItemsCustomSelect } from '@/components/molecules/DynamicItemsCustomSelect';
import { MultipleSelectionCustomSelect } from '@/components/molecules/MultipleSelectionCustomSelect';
import { StaticItemsCustomSelect } from '@/components/molecules/StaticItemsCustomSelect';
import { isItemsResolver, ItemsResolver, SelectItem } from '@/types/fields';

interface DynamicItemsProps<ItemValueType extends string | number = string> {
  readonly name?: string;
  readonly label?: string;
  readonly source: ItemsResolver<ItemValueType>;
  readonly defaultValue?: ItemValueType | '';
  readonly value?: ItemValueType | '';
  readonly multiple?: undefined;
}

interface MultipleSelectionProps<ItemValueType extends string | number = string> {
  readonly name?: string;
  readonly label?: string;
  readonly source: ItemsResolver<ItemValueType> | readonly SelectItem<ItemValueType>[];
  readonly defaultValue?: unknown;
  readonly multiple: true;
}

interface StaticItemsProps<ItemValueType extends string | number = string> {
  readonly name?: string;
  readonly label?: string;
  readonly source: readonly SelectItem<ItemValueType>[];
  readonly defaultValue?: unknown;
  readonly value?: unknown;
  readonly multiple?: undefined;

  onChange?(event: ItemValueType | ''): void;
}

interface Props<ItemValueType extends string | number = string> {
  readonly name?: string;
  readonly label?: string;
  readonly source: ItemsResolver<ItemValueType> | readonly SelectItem<ItemValueType>[];
  readonly defaultValue?: unknown;
  readonly value?: unknown;
  readonly multiple?: true | undefined;

  onChange?(event: ItemValueType | ''): void;
}

export function GenericSelect<T extends string | number = string>(
  props: Props<T>,
): React.ReactElement {
  if (isDynamicProps(props)) {
    const { name, label, source, defaultValue, value } = props;
    if (
      (defaultValue !== undefined && !isValidSelectValue(defaultValue)) ||
      (value !== undefined && !isValidSelectValue(value))
    ) {
      throw new Error(`invalid select value "${value ? value : defaultValue}"`);
    }

    return (
      <DynamicItemsCustomSelect
        name={name}
        label={label}
        itemResolver={source}
        defaultValue={defaultValue}
        value={value}
      />
    );
  } else if (isMultipleProps(props)) {
    const { name, label, source, defaultValue } = props;
    if (!isValidMultipleSelectValue(defaultValue)) {
      throw new Error('invalid multiple select value');
    }

    return (
      <MultipleSelectionCustomSelect
        name={name}
        label={label}
        source={source}
        defaultValue={defaultValue}
      />
    );
  } else if (isStaticProps(props)) {
    const { name, label, source, defaultValue, value, onChange } = props;
    if (
      (defaultValue !== undefined && !isValidSelectValue(defaultValue)) ||
      (value !== undefined && !isValidSelectValue(value))
    ) {
      throw new Error(`invalid select value "${value ? value : defaultValue}"`);
    }

    return (
      <StaticItemsCustomSelect
        name={name}
        label={label}
        items={source}
        defaultValue={defaultValue}
        value={value}
        onChange={onChange}
      />
    );
  } else {
    throw new Error('Cannot render a select with provided props');
  }
}

const isDynamicProps = <T extends string | number>(
  value: Props<T>,
): value is DynamicItemsProps<T> => {
  if (!isItemsResolver(value.source)) {
    return false;
  }

  if (value.defaultValue !== '' && Array.isArray(value.defaultValue)) {
    return false;
  }

  return !value.multiple;
};

const isMultipleProps = <T extends string | number>(
  value: Props<T>,
): value is MultipleSelectionProps<T> => {
  return value.multiple === true;
};

const isStaticProps = <T extends string | number>(value: Props<T>): value is StaticItemsProps<T> =>
  !value.multiple && !isItemsResolver(value.source);

function isValidSelectValue<ItemValueType extends string | number>(
  value: unknown,
): value is ItemValueType | '' {
  // Note that value can be null, this is acceptable within the actual components that
  // render the content
  return value === null || value === '' || typeof value === 'string' || typeof value === 'number';
}

function isValidMultipleSelectValue<ItemValueType extends string | number>(
  value: unknown,
): value is readonly ItemValueType[] | '' | undefined {
  return (
    value === undefined ||
    value === '' ||
    (Array.isArray(value) &&
      value.every(
        (item: unknown): item is string | number =>
          typeof item === 'string' || typeof item === 'number',
      ))
  );
}
